import { useEffect, useRef, useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { Falha, Girando } from "../../components/ui.js";

/**
 * O formulário de cartão, desenhado pelo Mercado Pago.
 *
 * O número do cartão é digitado dentro de um iframe deles e vira um **token**
 * antes de sair do navegador. O PAN nunca passa pelo nosso servidor — é o que
 * nos mantém fora do escopo de PCI, e o motivo de não termos um `<input>` de
 * cartão nosso em lugar nenhum.
 */

const SDK = "https://sdk.mercadopago.com/js/v2";

export interface DadosDoCartao {
  token: string;
  parcelas: number;
  metodoPagamentoId: string;
  emissorId?: string;
  documentoTipo?: string;
  documentoNumero?: string;
}

interface BrickController {
  unmount(): void;
}

interface MpBricks {
  create(
    tipo: "cardPayment",
    alvoId: string,
    opts: {
      initialization: { amount: number };
      callbacks: {
        onReady?: () => void;
        onError?: (erro: { message?: string }) => void;
        onSubmit: (dados: {
          token?: string;
          installments?: number;
          payment_method_id?: string;
          issuer_id?: string;
          payer?: { identification?: { type?: string; number?: string } };
        }) => Promise<void>;
      };
    },
  ): Promise<BrickController>;
}

interface MercadoPagoCtor {
  new (publicKey: string, opts?: { locale?: string }): { bricks(): MpBricks };
}

declare global {
  interface Window {
    MercadoPago?: MercadoPagoCtor;
  }
}

let carregando: Promise<void> | null = null;

function carregarSdk(): Promise<void> {
  if (window.MercadoPago) return Promise.resolve();
  if (carregando) return carregando;

  carregando = new Promise<void>((resolve, reject) => {
    const el = document.createElement("script");
    el.src = SDK;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => {
      carregando = null;
      reject(new Error("Não foi possível carregar o formulário de pagamento."));
    };
    document.head.appendChild(el);
  });

  return carregando;
}

export function CartaoBrick({
  valorCentavos,
  processando,
  aoEnviar,
}: {
  valorCentavos: number;
  processando: boolean;
  aoEnviar: (dados: DadosDoCartao) => void;
}) {
  const alvoId = useRef(`brick-cartao-${Math.random().toString(36).slice(2)}`);
  const controller = useRef<BrickController | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [pronto, setPronto] = useState(false);

  const publico = trpc.config.getPublic.useQuery();
  const publicKey = publico.data?.mercadopagoPublicKey ?? "";

  useEffect(() => {
    if (publicKey === "") return;

    let vivo = true;

    void carregarSdk()
      .then(async () => {
        if (!vivo || !window.MercadoPago) return;

        const mp = new window.MercadoPago(publicKey, { locale: "pt-BR" });

        controller.current = await mp.bricks().create("cardPayment", alvoId.current, {
          // O Brick trabalha em unidades decimais; guardamos centavos inteiros.
          initialization: { amount: valorCentavos / 100 },
          callbacks: {
            onReady: () => setPronto(true),
            onError: (e) => setErro(e.message ?? "Não foi possível montar o formulário."),
            onSubmit: async (dados) => {
              if (!dados.token) {
                setErro("O cartão não pôde ser validado. Confira os dados.");
                return;
              }

              aoEnviar({
                token: dados.token,
                parcelas: dados.installments ?? 1,
                metodoPagamentoId: dados.payment_method_id ?? "",
                ...(dados.issuer_id ? { emissorId: dados.issuer_id } : {}),
                ...(dados.payer?.identification?.type
                  ? { documentoTipo: dados.payer.identification.type }
                  : {}),
                ...(dados.payer?.identification?.number
                  ? { documentoNumero: dados.payer.identification.number }
                  : {}),
              });
            },
          },
        });
      })
      .catch((e: Error) => setErro(e.message));

    return () => {
      vivo = false;
      // Desmontar é obrigação nossa: o Brick deixa listeners e um iframe vivos,
      // e trocar de pacote sem isto empilharia formulários invisíveis.
      controller.current?.unmount();
      controller.current = null;
    };
  }, [publicKey, valorCentavos]);

  if (publico.isLoading) return <Girando className="mx-auto my-8 size-6 text-texto-fraco" />;

  if (publicKey === "") {
    return (
      <Falha mensagem="Pagamento com cartão não está configurado. Use o PIX, ou fale com quem administra o sistema." />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {erro && <Falha mensagem={erro} />}
      {!pronto && !erro && <Girando className="mx-auto my-8 size-6 text-texto-fraco" />}

      {/* O Brick renderiza aqui dentro, num iframe do Mercado Pago. */}
      <div id={alvoId.current} />

      {processando && (
        <div className="flex items-center justify-center gap-2 text-[0.875rem] text-texto-suave">
          <Girando className="size-4" />
          Processando o pagamento…
        </div>
      )}
    </div>
  );
}
